/**
 * GroupInitialsIcon - Displays group initials with badges
 *
 * Features:
 * - Shows initials (e.g., "TS" for "Trading Squad")
 * - Bold text when unread messages
 * - Badge with count below
 * - "@" symbol for mentions
 * - Throbbing animation when active
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Colors } from '@/constants/colors';
import { getInitials } from '@/utils/stringUtils';

interface GroupInitialsIconProps {
  name: string;
  unreadCount?: number;
  hasMention?: boolean;
  isActive?: boolean;
  size?: number;
}

export const GroupInitialsIcon: React.FC<GroupInitialsIconProps> = ({
  name,
  unreadCount = 0,
  hasMention = false,
  isActive = false,
  size = 40,
}) => {
  const initials = getInitials(name);
  const hasUnread = unreadCount > 0;

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.iconCircle,
          { width: size, height: size, borderRadius: size / 2 },
          isActive && styles.iconCircleActive,
        ]}
      >
        <Text style={[styles.initials, { fontSize: size * 0.4 }, hasUnread && styles.initialsBold]}>
          {initials}
        </Text>
      </View>

      {hasUnread && (
        <View style={styles.badge}>
          <Text style={styles.badgeText}>
            {hasMention ? `@${unreadCount}` : unreadCount > 99 ? '99+' : String(unreadCount)}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  iconCircle: {
    backgroundColor: Colors.background.tertiary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconCircleActive: {
    // @ts-ignore - Web-only property
    animation: 'heartbeat 2s ease-in-out infinite',
  },
  initials: {
    fontWeight: '500',
    color: Colors.text.secondary,
    textAlign: 'center',
  },
  initialsBold: {
    fontWeight: '700',
  },
  badge: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    backgroundColor: Colors.accent.primary,
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    paddingHorizontal: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: Colors.text.primary,
  },
});
